import {
  CategorySection,
  CategoryViewResultTypenames,
  Query,
  SortBy,
  VendorItem,
} from '@varo/graphql-schema-types';
import { useLocalSearchParams } from 'expo-router';
import { PillConfigProps } from 'features/rewards/categoriesV2/BrowseByCategoryOffers/AutoScrollPillGroup';
import {
  CATEGORY_ALL_ID,
  CATEGORY_ALL_LABEL,
} from 'features/rewards/constants/constants';
import useRewardsLocation from 'features/rewards/hooks/useRewardsLocation';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import FetchPartnerOffersCategories from 'services/graphql/queries/FetchPartnerOffersCategories';
import getNearByOffers from 'services/graphql/queries/rewards/GetNearByOffers';
import theme from 'shared/styles/theme';
import { CombinedError, useQuery } from 'urql';

import { useStandardOffers } from './useStandardOffers';

export interface BrowseByCategoryDataResult {
  location: { latitude: number; longitude: number } | null;
  pillConfigs: PillConfigProps[];
  selectedCategoryId: string | null;
  selectedCategoryName: string;
  isLoading: boolean;
  hasError: boolean;
  vendorList: VendorItem[];
  sections: CategorySection[];
  offersLoading: boolean;
  offersError: CombinedError | undefined;
  refetch: () => void;
  hidePills: boolean;
}

export const useBrowseByCategoryData = (): BrowseByCategoryDataResult => {
  const { category, name, hidePills } = useLocalSearchParams<{
    category?: string;
    name?: string;
    hidePills?: string;
  }>();

  const isHidePillsBoolean = hidePills === 'true';

  const { t: translatedText } = useTranslation('rewards', {
    keyPrefix: 'browseByCategory',
  });

  const { coords, isLocationFetching } = useRewardsLocation();

  const [
    {
      data: allCategoriesData,
      fetching: categoriesLoading,
      error: categoriesError,
    },
  ] = useQuery<Query>({
    query: FetchPartnerOffersCategories,
    variables: {
      sortTopCategoryBy: SortBy.OfferOrder,
    },
    requestPolicy: 'cache-first',
  });

  const categoriesResponse = allCategoriesData?.myPartnerOffers?.categories;

  const getSelectedCategory = () => {
    if (category && name) {
      return { id: category, name };
    }

    if (!categoriesResponse || categoriesResponse.length === 0) {
      return { id: null, name: '' };
    }

    if (category) {
      if (category === CATEGORY_ALL_ID) {
        return { id: CATEGORY_ALL_ID, name: CATEGORY_ALL_LABEL };
      }

      const targetCategory = categoriesResponse.find(
        (cat) =>
          cat.id === category ||
          cat.name.toLowerCase() === category.toLowerCase(),
      );

      if (targetCategory) {
        return { id: targetCategory.id, name: targetCategory.name };
      }
    }

    return { id: CATEGORY_ALL_ID, name: CATEGORY_ALL_LABEL };
  };

  const selectedCategory = getSelectedCategory();
  const selectedCategoryId = selectedCategory.id;
  const selectedCategoryName = selectedCategory.name;

  const categoryItems =
    categoriesResponse?.map((category) => {
      return {
        id: category.id,
        labelText: category.name,
        iconKey: category?.iconKey ?? 'ELLIPSE',
        iconColor: category?.iconKey
          ? theme.color.text.primary
          : theme.color.interactive.secondaryPressed,
        testID: `category-pill-${category.id}`,
      };
    }) ?? [];

  const allPill = {
    id: CATEGORY_ALL_ID,
    labelText: CATEGORY_ALL_LABEL,
    iconKey: 'CASHBACK',
    iconColor: theme.color.text.primary,
    testID: `category-pill-${CATEGORY_ALL_ID}`,
  };

  const pillConfigs: PillConfigProps[] = [allPill, ...categoryItems];

  const preloadedVendorList = useStandardOffers(
    selectedCategoryId,
    coords,
    isHidePillsBoolean,
  );
  const [offersPaused, setOffersPaused] = useState(true);

  useEffect(() => {
    setOffersPaused(
      !!preloadedVendorList || !selectedCategoryId || isLocationFetching,
    );
  }, [selectedCategoryId, isLocationFetching, preloadedVendorList]);

  const [
    { data: offersData, fetching: offersLoading, error: offersError },
    reExecuteOffersQuery,
  ] = useQuery<Query>({
    query: getNearByOffers,
    variables: {
      categoryId: selectedCategoryId ?? CATEGORY_ALL_ID,
      latitude: coords?.latitude ?? 0,
      longitude: coords?.longitude ?? 0,
    },
    pause: offersPaused,
    requestPolicy: 'network-only',
  });

  let sections: CategorySection[] = [];
  let vendorList: VendorItem[] = [];

  if (preloadedVendorList) {
    vendorList = preloadedVendorList;
    const sectionTitle =
      selectedCategoryName === CATEGORY_ALL_LABEL
        ? CATEGORY_ALL_LABEL
        : `${translatedText('allPrefix')} ${selectedCategoryName}`;
    sections = [
      {
        id: 'partner-offers-section',
        title: sectionTitle,
        vendors: preloadedVendorList,
        __typename: 'CategorySection',
      },
    ];
  } else if (
    offersData?.categoryPage?.categoryView?.__typename ===
    CategoryViewResultTypenames.CategoryViewSuccess
  ) {
    sections = offersData.categoryPage.categoryView.sections ?? [];
    vendorList = sections.flatMap((section) => section.vendors ?? []);
  }

  const isLoading =
    categoriesLoading && !categoriesResponse && !preloadedVendorList;
  const isOffersLoading =
    (offersLoading || isLocationFetching) && !preloadedVendorList;
  const hasError = Boolean(categoriesError) && !categoriesLoading;

  const refetch = useCallback(() => {
    if (!offersPaused && !preloadedVendorList) {
      reExecuteOffersQuery({ requestPolicy: 'network-only' });
    }
  }, [offersPaused, reExecuteOffersQuery, preloadedVendorList]);

  return {
    location: coords,
    pillConfigs,
    selectedCategoryId,
    selectedCategoryName,
    isLoading,
    hasError,
    vendorList,
    sections,
    offersLoading: isOffersLoading,
    offersError,
    refetch,
    hidePills: isHidePillsBoolean,
  };
};
